/**
 * Tiny presentational form widgets shared across the admin Settings tab.
 * Extracted from AdminDashboard.jsx to keep that file from growing past
 * ~2k lines. Pure UI — no data fetching here.
 */
import React from "react";
import { useApp } from "../../AppContext";

export const Field = ({ label, value, onChange, type = "text", testId, required }) => (
    <div>
        <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-2">
            {label}
            {required && <span className="text-terracotta-500"> *</span>}
        </label>
        <input
            data-testid={testId}
            type={type}
            value={value ?? ""}
            onChange={(e) =>
                onChange(type === "number" ? parseFloat(e.target.value) || 0 : e.target.value)
            }
            required={required}
            step={type === "number" ? "0.01" : undefined}
            className="w-full bg-earth-50 border border-earth-200 rounded-xl px-4 py-3 outline-none focus:border-terracotta-500"
        />
    </div>
);

export const TextArea = ({ label, value, onChange, testId, rows = 3, placeholder = "" }) => (
    <div>
        <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-2">
            {label}
        </label>
        <textarea
            data-testid={testId}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            rows={rows}
            placeholder={placeholder}
            className="w-full bg-earth-50 border border-earth-200 rounded-xl px-4 py-3 outline-none focus:border-terracotta-500 resize-y"
        />
    </div>
);

export const Toggle = ({ checked, onChange, testId, labelOn = "Activo", labelOff = "Inactivo" }) => (
    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
        <input
            type="checkbox"
            data-testid={testId}
            checked={!!checked}
            onChange={(e) => onChange(e.target.checked)}
            className="sr-only peer"
        />
        <span className="relative w-11 h-6 rounded-full bg-earth-300 peer-checked:bg-leaf-500 transition-colors after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-5" />
        <span className="text-xs font-bold uppercase tracking-wider text-earth-700">
            {checked ? labelOn : labelOff}
        </span>
    </label>
);

export const SectionCard = ({ icon: Icon, title, subtitle, children, accent = "#C85A17" }) => (
    <div className="bg-white rounded-3xl border border-earth-200 p-6 shadow-sm space-y-4">
        <div className="flex items-center gap-3">
            <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: `${accent}1A` }}
            >
                <Icon className="w-5 h-5" style={{ color: accent }} />
            </div>
            <div>
                <h3 className="font-serif text-xl font-bold text-earth-900">{title}</h3>
                {subtitle && <p className="text-xs text-earth-600">{subtitle}</p>}
            </div>
        </div>
        {children}
    </div>
);

export const ColorField = ({ label, value, onChange, testId }) => (
    <div>
        <label className="block text-[10px] uppercase tracking-wider font-bold text-earth-500 mb-1">
            {label}
        </label>
        <div className="flex items-center gap-2">
            <input
                type="color"
                value={(value || "#000000").toUpperCase()}
                onChange={(e) => onChange(e.target.value.toUpperCase())}
                data-testid={`${testId}-picker`}
                className="w-12 h-10 rounded-xl cursor-pointer border border-earth-200"
            />
            <input
                type="text"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                data-testid={`${testId}-hex`}
                placeholder="#C85A17"
                className="flex-1 px-3 py-2 rounded-xl border border-earth-200 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-200 outline-none text-sm font-mono uppercase"
            />
        </div>
    </div>
);

export const PaymentBlock = ({ title, enabled, onToggle, children, testIdPrefix }) => {
    const { t } = useApp();
    return (
        <div
            className={`rounded-2xl border p-5 transition-colors ${
                enabled ? "border-leaf-300 bg-leaf-50/40" : "border-earth-200 bg-earth-50/40 opacity-80"
            }`}
        >
            <div className="flex items-center justify-between gap-4 mb-3">
                <div className="font-serif font-bold text-earth-900">{title}</div>
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        data-testid={`settings-${testIdPrefix}-enabled`}
                        checked={!!enabled}
                        onChange={(e) => onToggle(e.target.checked)}
                        className="sr-only peer"
                    />
                    <span className="relative w-11 h-6 rounded-full bg-earth-300 peer-checked:bg-leaf-500 transition-colors after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-5" />
                    <span className="text-xs font-bold uppercase tracking-wider text-earth-700">
                        {enabled ? t.admin.enabled : t.admin.disabled}
                    </span>
                </label>
            </div>
            {children && <div className="grid sm:grid-cols-2 gap-4">{children}</div>}
        </div>
    );
};

export const Modal = ({ children, onClose, title }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-earth-900/70 backdrop-blur-sm">
        <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-earth-100 px-7 py-5 flex items-center justify-between">
                <h3 className="font-serif font-bold text-xl text-earth-900">{title}</h3>
                <button
                    onClick={onClose}
                    className="p-2 rounded-full hover:bg-earth-100"
                    aria-label="Close"
                >
                    ✕
                </button>
            </div>
            <div className="p-7">{children}</div>
        </div>
    </div>
);
